'use client';

import type { EmblaCarouselType } from 'embla-carousel';
import { useState } from 'react';

import { Button } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { HeaderLevel2 } from '@/app/components/headers/header-level-2';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import { generateSources } from '@/app/components/media/image/generate-sources';
import { Sliders } from '@/app/components/sliders';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import styles from '@/app/cv/components/sections/products/index.module.css';
import { ProductBadge } from '@/app/cv/components/sections/products/shared/badge';
import type { ProductBaseSectionProps } from '@/app/cv/components/sections/products/types';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export const ProductHighlight = ({
  items = [],
  lead,
  title,
  description,
  buttons = [],
  testId = 'product-highlight',
}: ProductBaseSectionProps) => {
  useRevealAnim({ defaultBaseDelay: 0.04 });

  const hasButtons = buttons.length > 0;
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const { selectedIndex } = usePaginationSlider(emblaApi);

  if (items.length === 0) return null;

  return (
    <Section
      size="lg"
      className="from-silver-200 bg-linear-to-t to-white"
      data-revealanim-group
      {...createDataAttributes({ test: `${testId}-section` })}
    >
      <Container className="mb-6 lg:mb-10">
        <HeaderLevel2
          lead={lead}
          title={title || ''}
          theme="light"
          content2ClassName="space-y-2"
          {...(description && {
            content1: {
              content: description,
            },
          })}
          buttons={buttons.map((btn) => ({
            ...btn,
            className: cn('max-lg:hidden', btn.className),
          }))}
        />
      </Container>

      <Sliders
        onEmblaInit={setEmblaApi}
        align="center"
        loop
        containerClassName="gap-x-2 md:gap-x-4 xl:gap-x-6"
      >
        {items.map((item, index) => {
          const badge = item.badge?.label ? item.badge : null;

          return (
            <div
              key={index}
              className={cn(styles.highlightSlide, 'flex-[0_0_100%]', {
                'mr-2 lg:mr-6': index === items.length - 1,
              })}
              {...createDataAttributes({
                test: `slide-${index}`,
                active: index === selectedIndex ? true : undefined,
              })}
            >
              <div className="overflow-hidden rounded-sm md:rounded-md">
                {!!item.image && (
                  <Image
                    src={item.image.src}
                    alt={item.image.alt || item.title || ''}
                    width={1024}
                    height={576}
                    aspectRatio="aspect-16-9"
                    sources={[
                      {
                        srcset: item.image.src,
                        media: { minWidth: 1024 },
                        options: { width: 1024, crop: '16:9' },
                      },
                      ...generateSources('tablet', {
                        mobileSrc: item.image.src,
                        mobileOptions: { crop: '16:9' },
                      }),
                      ...generateSources('mobile', {
                        mobileSrc: item.image.src,
                        mobileOptions: { crop: '16:9' },
                      }),
                    ]}
                  />
                )}
              </div>
              <div
                className={cn('property-transforms-opacity mt-6', {
                  'delay-300 duration-700': index === selectedIndex,
                  'translate-x-10 opacity-0 duration-0':
                    index !== selectedIndex,
                })}
                {...createDataAttributes({ test: 'content' })}
              >
                <div
                  className={cn({
                    'flex flex-col gap-4': !!badge,
                  })}
                >
                  {!!badge && <ProductBadge {...badge} theme="dark" />}
                  <h3
                    className="text-sub-h2 font-noto-bold"
                    dangerouslySetInnerHTML={{ __html: item.title || '' }}
                    {...createDataAttributes({ test: 'title' })}
                  />
                  {!!item.description && (
                    <div
                      className={cn('text-body-1 text-gray-700', {
                        'mt-2': !badge,
                      })}
                      dangerouslySetInnerHTML={{ __html: item.description }}
                      {...createDataAttributes({ test: 'description' })}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </Sliders>

      {!!hasButtons && (
        <Container className="mt-6 sm:mt-10 lg:hidden" data-revealanim>
          <ButtonGroup>
            {buttons.map((btn, index) => (
              <Button
                key={index}
                {...btn}
                className={cn('w-full md:w-auto', btn.className)}
              />
            ))}
          </ButtonGroup>
        </Container>
      )}
    </Section>
  );
};
